import axios from 'axios';
import { DiscussionMessage, DiscussionPage, SendDiscussionMessageInput } from '../domain/types';
import { DiscussionRepository } from '../application/ports';

interface ApiRepositoryDeps {
  baseUrl: string;
  getHeaders: () => Promise<Record<string, string>>;
}

interface DiscussionMessageDto {
  id: string;
  entityType: string;
  entityId: string;
  senderUserId: string;
  senderDisplayName: string;
  message: string;
  createdAt: string;
  updatedAt?: string;
  clientMessageId?: string;
}

interface DiscussionMessagesResponse {
  items: DiscussionMessageDto[];
  nextCursor?: string;
}

const toDomain = (dto: DiscussionMessageDto): DiscussionMessage => ({
  id: dto.id,
  entityType: dto.entityType,
  entityId: dto.entityId,
  senderId: dto.senderUserId,
  senderDisplayName: dto.senderDisplayName,
  text: dto.message,
  createdAt: dto.createdAt,
  updatedAt: dto.updatedAt,
  clientMessageId: dto.clientMessageId,
});

export const createApiDiscussionRepository = ({ baseUrl, getHeaders }: ApiRepositoryDeps): DiscussionRepository => ({
  getMessages: async (ref, cursor, take = 50): Promise<DiscussionPage> => {
    const headers = await getHeaders();
    const params = new URLSearchParams();
    params.set('take', String(take));
    if (cursor) {
      params.set('cursor', cursor);
    }

    const response = await axios.get<DiscussionMessagesResponse>(
      `${baseUrl}/api/discussions/${encodeURIComponent(ref.entityType)}/${encodeURIComponent(String(ref.entityId))}?${params.toString()}`,
      { headers },
    );

    return {
      items: (response.data.items || []).map(toDomain),
      nextCursor: response.data.nextCursor,
    };
  },
  sendMessage: async (ref, input: SendDiscussionMessageInput): Promise<DiscussionMessage> => {
    const headers = await getHeaders();

    const response = await axios.post<DiscussionMessageDto>(
      `${baseUrl}/api/discussions/${encodeURIComponent(ref.entityType)}/${encodeURIComponent(String(ref.entityId))}`,
      {
        message: input.text,
        clientMessageId: input.clientMessageId,
      },
      { headers },
    );

    return toDomain(response.data);
  },
});
